import { useEffect, useState } from 'react'

/**
 * a simple hook that debounces the supplied value for as long as desired
 *
 * also includes whether the value is debouncing
 *
 * @param value the value to debounce
 * @param ms how long to debounce for. defaults to `500`
 * @returns a tuple where the first value is the debounced value and the second is whether it's currently debouncing
 */
export function useDebounce<T>(value: T, ms = 500): [T, boolean] {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)
  const [debouncing, setDebouncing] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setDebouncing(true)
    const debounce = setTimeout(() => {
      setDebouncedValue(value)
      setDebouncing(false)
    }, ms)
    return () => {
      clearTimeout(debounce)
    }
  }, [value, ms])

  return [debouncedValue, debouncing]
}
